import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ADMIN } from '@/constants/testIds';

const EMPTY = {
  name: '', code: '', city: '', address: '', phone: '', region: '', cluster: '',
  color: '#292524', active: true, operating_hours: [],
};

export default function ClinicsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/clinics'); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...EMPTY, ...r });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { ...form };
      if (editing) await api.patch(`/clinics/${editing.id}`, payload);
      else await api.post('/clinics', payload);
      toast.success('Saved');
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    try { await api.delete(`/clinics/${r.id}`); load(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <AdminHeader
        title="Clinics"
        description="Locations where appointments are hosted."
        onCreate={openCreate}
        createLabel="New Clinic"
        createTestId={ADMIN.createButton('clinic')}
      />
      <AdminTable
        loading={loading}
        rows={rows}
        columns={[
          { key: 'name', label: 'Name', render: (r) => (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: r.color }} />
              <span className="font-medium">{r.name}</span>
            </div>
          )},
          { key: 'code', label: 'Code' },
          { key: 'city', label: 'City' },
          { key: 'region', label: 'Region' },
          { key: 'cluster', label: 'Cluster' },
          { key: 'phone', label: 'Phone' },
        ]}
        rowTestId={ADMIN.row}
        onEdit={openEdit}
        onDelete={remove}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Clinic' : 'New Clinic'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {['name','code','city','address','phone','region','cluster'].map((k) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider text-stone-600">{k}</Label>
                <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9" data-testid={`clinic-field-${k}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Colour</Label>
              <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-20 p-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
